<script setup lang="ts">
import { computed } from 'vue'

import type { ChartType, ChartTypeMeta } from '../../models/chart.types'
import ReportToolbarSelect from '../controls/ReportToolbarSelect.vue'

import { useI18n } from '@/shared/i18n'
import { Select, type IconName, type SelectOption } from '@/shared/ui'

const model = defineModel<ChartType>({ required: true })
const props = withDefaults(defineProps<{ types: readonly ChartTypeMeta[]; compact?: boolean }>(), {
  compact: false,
})
const { t } = useI18n()

/** Font Awesome Pro glyphs mirrored from new-front's CHART_ICONS catalog (same kit). */
const CHART_TYPE_ICONS: Partial<Record<ChartType, IconName>> = {
  compare_column: 'chart',
  compare_bar: 'chart-bar',
  line: 'chart-line',
  area: 'chart-area',
  stacked_area: 'chart-area',
  multi_line: 'chart-line',
  column: 'chart',
  bar: 'chart-bar',
  stacked_column: 'chart',
  stacked_bar: 'chart-bar',
  pie: 'chart-pie',
  multi_pie: 'chart-pie',
  donut: 'chart-donut',
  scatter: 'chart-scatter',
  bubble: 'chart-scatter-bubble',
  packed_bubble: 'chart-scatter-bubble',
  heatmap: 'table-cells-large',
  treemap: 'grid-2',
  sunburst: 'sun',
  funnel: 'filter-circle-dollar',
  waterfall: 'chart-waterfall',
  radar: 'radar',
  gauge: 'gauge-high',
  sankey: 'diagram-predecessor',
  lollipop: 'lollipop',
  combination: 'chart-mixed',
  multi_metric: 'chart-bar',
  dumbbell: 'chart-bar',
  bullet: 'chart-bar',
  diverging_bar: 'chart-bar',
}

const options = computed<SelectOption<ChartType>[]>(() =>
  props.types.map((meta) => ({
    value: meta.type,
    label: t(meta.nameKey),
    meta: {
      backendLabel: meta.label,
      description: meta.description,
      visual: { icon: CHART_TYPE_ICONS[meta.type] ?? 'chart' },
    },
  })),
)
</script>

<template>
  <ReportToolbarSelect
    v-if="compact"
    v-model="model"
    :options="options"
    :label="t('reports.views.chart')"
    :placeholder="t('reports.chartTypes.placeholder')"
    :searchable="types.length > 7"
    icon="chart"
    panel-width="min(20rem, calc(100vw - 0.75rem))"
  />
  <Select
    v-else
    v-model="model"
    size="sm"
    :label="t('reports.chartTypes.label')"
    :placeholder="t('reports.chartTypes.placeholder')"
    :options="options"
    :searchable="types.length > 7"
  />
</template>
